'use client'

import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalTitle,
  ResponsiveModalTrigger,
} from '@/components/extension/responsive-modal'
import { Button } from '@/components/ui/button'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { sanitize } from '@/lib/helpers/sanitize'
import { cn, stringToSlug } from '@/lib/utils'
import { TRecruitment } from '@/types/page'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'

export const JobDetailModal = ({
  data,
  isClose,
  isOpenDefault,
}: {
  data: TRecruitment
  isClose: boolean
  isOpenDefault?: boolean
}) => {
  const t = useTranslations()
  const [isOpen, setIsOpen] = useState(isOpenDefault)
  const jobSlug = stringToSlug(data.title)
  const baseUrl = '/lp/coolmate-101'
  const jobUrl = `${baseUrl}?job=${jobSlug}`

  const applyUrl = (() => {
    const positionName = data.position_name || data.title
    if (!data.url || !positionName) return data.url

    try {
      const url = new URL(data.url)
      url.searchParams.set('prefill_Vị trí Bạn muốn ứng tuyển', positionName)
      return url.toString()
    } catch {
      return data.url
    }
  })()

  const handleOpenJobDetail = () => {
    window?.history.pushState(null, '', jobUrl)
  }

  return (
    <ResponsiveModal
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open)
        if (!open) {
          window?.history.pushState(null, '', baseUrl)
        }
      }}
    >
      <ResponsiveModalTrigger asChild>
        <button
          onClick={handleOpenJobDetail}
          className={cn(
            'text-xs text-primary hover:text-neutral-900',
            isOpenDefault && 'hidden'
          )}
        >
          {t(
            'landing_coolmate_101_page.recruitment.job_item.details_and_apply'
          )}{' '}
          <Image
            src={ImageHelper.getImageUrl(
              '/uploads/November2021/right-arrows(1).png'
            )}
            width={11}
            height={11}
            alt='arrow-right'
            className='inline'
          />
        </button>
      </ResponsiveModalTrigger>

      <ResponsiveModalContent
        className='z-[103] gap-0 border-none px-0 py-8 pb-4 lg:max-w-[1200px]'
        aria-describedby={'modal-change-password'}
        overlayClassName='z-[103]'
      >
        <ResponsiveModalTitle />

        <ResponsiveModalDescription />
        <div className='max-h-[80vh] gap-5 overflow-y-auto px-5 pb-2 lg:flex'>
          <div className='h-fit rounded-2xl border-2 border-primary p-4'>
            <h4 className='text-lg font-bold'>{data.title}</h4>
            <p className='mb-3 font-sans text-sm italic text-neutral-500'>
              {data.benefits}
            </p>

            <p className='mb-3 text-sm text-neutral-900'>{data.requirements}</p>
            <div className='text-sm'>
              {t('landing_coolmate_101_page.recruitment.job_detail.contact')}:{' '}
              <br />
              <Link href='mailto: tuyendung@coolmate.me'>
                <b>tuyendung@coolmate.me</b>
              </Link>{' '}
              {/* {t('landing_coolmate_101_page.recruitment.job_detail.or')}{' '}
              <Link href='tel: 0793001378'>
                <b>0793001378</b>
              </Link> */}
              <div className='mt-6 grid grid-cols-2 gap-4 border-t border-t-neutral-200 pt-6'>
                <div className='col-span-2'>
                  {t(
                    'landing_coolmate_101_page.recruitment.job_detail.department_label'
                  )}
                  :
                  <br />
                  <p className='text-sm font-bold text-neutral-900'>
                    {data.department_details}
                  </p>
                </div>
                {data.department && (
                  <div className='lg:col-span-2'>
                    {t(
                      'landing_coolmate_101_page.recruitment.job_detail.team_label'
                    )}
                    :
                    <br />
                    <p className='text-sm font-bold text-neutral-900'>
                      {data.department}
                    </p>
                  </div>
                )}

                <div className='text-sm text-neutral-900'>
                  {t(
                    'landing_coolmate_101_page.recruitment.job_detail.location_label'
                  )}
                  : <br />
                  <strong>{data.address}</strong>
                </div>
                <div className='text-sm text-neutral-900'>
                  {t(
                    'landing_coolmate_101_page.recruitment.job_detail.salary_label'
                  )}
                  : <br />
                  <strong>{data.salary}</strong>
                </div>
                <div className='text-sm text-neutral-900'>
                  {t(
                    'landing_coolmate_101_page.recruitment.job_detail.working_time_label'
                  )}
                  : <br />
                  <strong>{data.format}</strong>
                </div>
                <div className='text-sm text-neutral-900'>
                  {t(
                    'landing_coolmate_101_page.recruitment.job_detail.quantity_label'
                  )}
                  : <br />
                  <strong>{data.number}</strong>
                </div>
                <div className='text-sm text-neutral-900'>
                  {t(
                    'landing_coolmate_101_page.recruitment.job_detail.deadline_label'
                  )}
                  : <br />
                  <strong>{data.deadline}</strong>
                </div>
              </div>
            </div>
          </div>

          <div className='mt-4 flex flex-col lg:mt-0 lg:w-4/6'>
            <p className='pb-3 text-lg font-bold text-neutral-900'>
              {t(
                'landing_coolmate_101_page.recruitment.job_detail.job_description'
              )}
            </p>
            <div
              dangerouslySetInnerHTML={{
                __html: sanitize(data.description || ''),
              }}
              className='rich-html overflow-y-auto lg:max-h-[calc(100vh-230px)]'
            ></div>
            <div className='pt-4'>
              <Button variant={'primary'} size={'small'} asChild>
                <Link href={applyUrl} target='_blank' rel='noopener noreferrer'>
                  {!isClose
                    ? t(
                        'landing_coolmate_101_page.recruitment.job_detail.save_profile'
                      )
                    : t(
                        'landing_coolmate_101_page.recruitment.job_detail.submit_profile'
                      )}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </ResponsiveModalContent>
    </ResponsiveModal>
  )
}
